<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1200"
    v-if="serverDataLoaded"
  >
    <div class="bg-white poppins">
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ $t("add_new_sku") }}</h4>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <!--begin::Body-->
        <div>
          <div class="row ma-0">
            <div class="h-75px py-0 col-12 col-sm-6" v-if="!isClient">
              <v-select
                v-model="formData.customer_id"
                :label="$t('client')"
                item-text="text"
                item-value="index"
                :items="serverData.clients"
                clearable
                outlined
                dense
                :error-messages="customer_idErrors"
                @input="$v.formData.customer_id.$touch()"
                @blur="$v.formData.customer_id.$touch()"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6">
              <v-select
                v-model="formData.sku_type_id"
                :label="$t('sku_type')"
                item-text="text"
                item-value="index"
                :items="serverData.sku_types"
                clearable
                outlined
                dense
                :error-messages="sku_type_idErrors"
                @input="$v.formData.sku_type_id.$touch()"
                @blur="$v.formData.sku_type_id.$touch()"
              />
            </div>

            <div
              class="h-75px py-0 col-12 col-sm-6"
              :class="[isClient ? '' : 'col-md-4']"
            >
              <v-text-field
                v-model="formData.system_code"
                :label="$t('system_code')"
                clearable
                outlined
                dense
                :error-messages="system_codeErrors"
                @input="$v.formData.system_code.$touch()"
                @blur="$v.formData.system_code.$touch()"
              />
            </div>
            <div
              class="h-75px py-0 col-12 col-sm-6"
              :class="[isClient ? '' : 'col-md-4']"
            >
              <v-text-field
                v-model="formData.alternative_sku_name"
                :label="$t('sku_label')"
                clearable
                outlined
                dense
              />
            </div>
            <div
              class="h-75px py-0 col-12 col-sm-6"
              :class="[isClient ? '' : 'col-md-4']"
            >
              <v-text-field
                v-model="formData.brand"
                :label="$t('brand')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.width"
                :label="$t('width')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('width', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.length"
                :label="$t('length')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('length', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.height"
                :label="$t('height')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('height', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.weight"
                :label="$t('weight')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('weight', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.cube"
                :label="$t('cube')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('cube', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.hs_code"
                :label="$t('hs_code')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12">
              <v-select
                multiple
                v-model="formData.categories"
                :label="$t('categories')"
                item-text="text"
                item-value="index"
                :items="serverData.groups"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.country_id"
                :label="$t('country_of_origin')"
                item-text="text"
                item-value="index"
                :items="serverData.countries"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.currency_id"
                :label="$t('currency')"
                item-text="text"
                item-value="index"
                :items="serverData.currencies"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.price"
                :label="$t('price')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('price', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.uom_type_id"
                :label="$t('uom_type')"
                item-text="text"
                item-value="index"
                :items="serverData.uom_types"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.cost_price"
                :label="$t('cost_price')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('cost_price', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.landed_cost"
                :label="$t('landed_cost')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('landed_cost', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.dg_type_id"
                :label="$t('dg_type')"
                item-text="text"
                item-value="index"
                :items="serverData.dg_types"
                clearable
                outlined
                dense
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.count_in_box"
                :label="$t('count_in_box')"
                type="number"
                clearable
                outlined
                dense
                min="0"
              />
            </div>

            <div
              class="h-75px py-0 col-12"
              :class="[isClient ? '' : 'col-md-6']"
            >
              <v-text-field
                v-model="formData.out_of_stock_threshold"
                :label="$t('out_of_stock_notification_threshold')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('out_of_stock_threshold', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-md-6" v-if="!isClient">
              <v-select
                v-model="formData.storage_type"
                :label="$t('storage_type')"
                item-text="text"
                item-value="index"
                :items="serverData.storage_types"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12">
              <v-text-field
                v-model="formData.dangerous_goods_note"
                :label="$t('dangerous_goods_note')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12">
              <v-text-field
                v-model="formData.description"
                :label="$t('description')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="py-0 col-12">
              <v-file-input
                v-model="formData.photo"
                show-size
                counter
                multiple
                dense
                outlined
                accept="image/png, image/jpeg, image/bmp"
                prepend-icon="mdi-camera"
                :label="$t('photo')"
                clearable
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0 pl-3"
            >
              <h5 class="pr-4 pt-5 font-weight-regular">Expiry control</h5>
              <v-switch
                v-model="formData.expiry_control"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("serial_number_control") }}
              </h5>
              <v-switch
                v-model="formData.serial_number_control"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("temperature_control") }}
              </h5>
              <v-switch
                v-model="formData.temperature_control"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("preorder_enable") }}
              </h5>
              <v-switch
                v-model="formData.is_pre_order_enabled"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pr-4 pt-5 font-weight-regular">Shipping required</h5>
              <v-switch
                v-model="formData.is_shipping_required"
                hide-details
                dense
                inset
              />
            </div>
          </div>

          <div class="mt-4 pa-0 w-100">
            <SkuDatatable :setSku="setSkus" :codes="formData.codes" />
          </div>
        </div>

        <!--end::Body-->
        <div class="my-4 h-75px d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light me-3 px-5 py-3 ls1"
            @click="resetCreateForm"
          >
            {{ $t("clear") }}
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitCreateForm"
          >
            {{ $t("submit") }}
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import ApiService from "@/core/services/api.service";
import SkuDatatable from "@/own/components/fulfillment/skus/skuAddContainer/SkuDatatable";
import SwalAdded from "@/core/services/AlertServices/SwalAdded";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";
export default {
  mixins: [validationMixin, fieldValueValidation],
  components: {
    SkuDatatable,
  },
  validations: {
    formData: {
      customer_id: {
        required: requiredIf(function () {
          return !this.isClient;
        }),
      },
      system_code: { required },
      sku_type_id: { required },
    },
  },
  name: "AddItem",
  props: ["refresher", "pageLoader"],
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      customer_id: null,
      system_code: null,
      sku_type_id: null,
      alternative_sku_name: null,
      description: null,
      weight: null,
      cube: null,
      length: null,
      width: null,
      height: null,
      brand: null,
      categories: [],
      codes: [],
      photo: null,
      expiry_control: 0,
      serial_number_control: 0,
      status: null,
      hs_code: null,
      country_id: null,
      currency_id: null,
      price: null,
      cost_price: null,
      landed_cost: null,
      uom_type_id: null,
      dg_type_id: null,
      dangerous_goods_note: null,
      temperature_control: 0,
      is_pre_order_enabled: 0,
      count_in_box: null,
      out_of_stock_threshold: null,
      storage_type: 1,
      is_shipping_required: 1,
    },
  }),
  methods: {
    handleFormValidation(fieldName, vueObj) {
      const errors = [];
      if (!vueObj.$v.formData[fieldName].$dirty) return errors;

      if ("required" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].required &&
          errors.push(this.$t("this_field_is_required"));
      }
      return errors;
    },
    loadDataFromServer() {
      this.pageLoader(true);
      ApiService.post("/fulfillment/skus/create")
        .then((response) => {
          this.serverData = response.data;
          this.pageLoader(false);
        })
        .catch(() => {
          this.dialog = false;
          this.loader(false);
        });
    },

    toggleModal() {
      if (this.dialog) this.resetCreateForm();
      else this.loadDataFromServer();
      this.dialog = !this.dialog;
    },
    convertToFormData() {
      // console.log(this.formData);
      let data = new FormData();
      Object.entries(this.formData).forEach((item) => {
        if (
          (item[1] && item[0] !== "image" && item[0] !== "photo") ||
          item[1] === 0
        ) {
          if (Array.isArray(item[1])) {
            item[1].forEach((it, index) => {
              if (typeof it === "object" && !Array.isArray(it) && it !== null) {
                Object.entries(it).forEach((el) => {
                  data.append(`${item[0]}[${index}][${el[0]}]`, el[1]);
                });
              } else {
                data.append(`${item[0]}[]`, it);
              }
            });
          } else {
            data.append(item[0], item[1]);
          }
        } else if (item[1] && (item[0] === "image" || item[0] === "photo")) {
          data.append(item[0], item[1][0]);
        }
      });
      return data;
    },
    setSkus(item) {
      this.formData.codes = [...item];
    },
    submitCreateForm() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.pageLoader(true);
      let data = this.convertToFormData();
      ApiService.post("/fulfillment/skus/store", data)
        .then(() => {
          SwalAdded();
          this.toggleModal();
          this.refresher();
          this.pageLoader(false);
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    resetCreateForm() {
      this.$v.$reset();
      this.formData = {
        customer_id: null,
        system_code: null,
        sku_type_id: null,
        alternative_sku_name: null,
        description: null,
        weight: null,
        cube: null,
        length: null,
        width: null,
        height: null,
        brand: null,
        categories: [],
        codes: [],
        expiry_control: 0,
        serial_number_control: 0,
        status: null,
        hs_code: null,
        country_id: null,
        currency_id: null,
        price: null,
        cost_price: null,
        landed_cost: null,
        uom_type_id: null,
        dg_type_id: null,
        dangerous_goods_note: null,
        temperature_control: 0,
        is_pre_order_enabled: 0,
        count_in_box: null,
        out_of_stock_threshold: null,
        storage_type: 1,
        is_shipping_required: 1,
      };
    },
  },
  computed: {
    isClient() {
      return this.$store.getters.currentUser.data.is_client;
      // return true;
    },
    serverDataLoaded: function () {
      return !!this.serverData;
    },
    customer_idErrors: function () {
      return this.handleFormValidation("customer_id", this);
    },
    system_codeErrors: function () {
      return this.handleFormValidation("system_code", this);
    },
    sku_type_idErrors: function () {
      return this.handleFormValidation("sku_type_id", this);
    },
  },
};
</script>
